import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  fetchProductReplacementReferencesApi,
  ProductReplacementPayload,
  ProductReplacementReference,
  ProductReplacementReferencesResponse,
  ReplacementReferenceType,
  replaceDispatchedProductApi,
} from "../../api/dispatchReplacement.api";

export interface FetchReplacementReferencesArgs {
  type: ReplacementReferenceType;
  search: string;
  page: number;
  limit: number;
}

export interface ReplacementReferenceQueryState {
  data: ProductReplacementReference[];
  pagination: ProductReplacementReferencesResponse["pagination"] | null;
  loading: boolean;
  error: string | null;
  requestId: string | null;
}

export interface DispatchReplacementState {
  referenceQueries: Record<string, ReplacementReferenceQueryState>;
  submissionStatus: "idle" | "pending" | "fulfilled" | "rejected";
  submissionError: string | null;
}

const initialState: DispatchReplacementState = {
  referenceQueries: {},
  submissionStatus: "idle",
  submissionError: null,
};

export const getReplacementReferenceQueryKey = (
  type: ReplacementReferenceType,
  search: string,
) => JSON.stringify([type, search]);

const getApiErrorMessage = (error: unknown, fallback: string) => {
  const apiError = error as {
    response?: {
      status?: number;
      data?: { message?: unknown; error?: unknown };
    };
    message?: string;
  };
  const message = apiError.response?.data?.message || apiError.response?.data?.error;
  if (typeof message === "string" && message.trim()) return message.trim();

  if (apiError.response?.status === 400 || apiError.response?.status === 422) {
    return "The replacement details are invalid. Check the serial numbers, reason, and beneficiary.";
  }
  if (apiError.response?.status === 404) {
    return "A serial number or beneficiary reference could not be found.";
  }
  if (apiError.response?.status === 409) {
    return "Replacement conflict: the old serial may already be dispatched or replacement stock may be unavailable.";
  }
  return apiError.message || fallback;
};

const createEmptyReferenceQuery = (): ReplacementReferenceQueryState => ({
  data: [],
  pagination: null,
  loading: false,
  error: null,
  requestId: null,
});

const uniqueReferences = (references: ProductReplacementReference[]) => {
  const seen = new Set<string>();
  return references.filter((reference) => {
    if (!reference._id || seen.has(reference._id)) return false;
    seen.add(reference._id);
    return true;
  });
};

export const fetchReplacementReferences = createAsyncThunk<
  ProductReplacementReferencesResponse,
  FetchReplacementReferencesArgs,
  { rejectValue: string }
>(
  "dispatchReplacement/fetchReferences",
  async ({ type, search, page, limit }, { rejectWithValue }) => {
    try {
      return await fetchProductReplacementReferencesApi(type, {
        search,
        page,
        limit,
      });
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to fetch replacement references."),
      );
    }
  },
);

export const submitProductReplacement = createAsyncThunk<
  unknown,
  ProductReplacementPayload,
  { rejectValue: string }
>(
  "dispatchReplacement/submitReplacement",
  async (payload, { rejectWithValue }) => {
    try {
      return await replaceDispatchedProductApi(payload);
    } catch (error) {
      return rejectWithValue(
        getApiErrorMessage(error, "Failed to replace product. Please try again."),
      );
    }
  },
);

const dispatchReplacementSlice = createSlice({
  name: "dispatchReplacement",
  initialState,
  reducers: {
    resetReplacementMutation(state) {
      state.submissionStatus = "idle";
      state.submissionError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReplacementReferences.pending, (state, action) => {
        const key = getReplacementReferenceQueryKey(
          action.meta.arg.type,
          action.meta.arg.search,
        );
        const query =
          state.referenceQueries[key] || createEmptyReferenceQuery();
        query.loading = true;
        query.error = null;
        query.requestId = action.meta.requestId;
        state.referenceQueries[key] = query;
      })
      .addCase(fetchReplacementReferences.fulfilled, (state, action) => {
        const key = getReplacementReferenceQueryKey(
          action.meta.arg.type,
          action.meta.arg.search,
        );
        const query = state.referenceQueries[key];
        if (!query || query.requestId !== action.meta.requestId) return;

        query.data = uniqueReferences(
          action.meta.arg.page === 1
            ? action.payload.data
            : [...query.data, ...action.payload.data],
        );
        query.pagination = action.payload.pagination;
        query.loading = false;
        query.error = null;
        query.requestId = null;
      })
      .addCase(fetchReplacementReferences.rejected, (state, action) => {
        const key = getReplacementReferenceQueryKey(
          action.meta.arg.type,
          action.meta.arg.search,
        );
        const query = state.referenceQueries[key];
        if (!query || query.requestId !== action.meta.requestId) return;

        query.loading = false;
        query.error =
          action.payload || action.error.message || "Failed to fetch references.";
        query.requestId = null;
      })
      .addCase(submitProductReplacement.pending, (state) => {
        state.submissionStatus = "pending";
        state.submissionError = null;
      })
      .addCase(submitProductReplacement.fulfilled, (state) => {
        state.submissionStatus = "fulfilled";
        state.submissionError = null;
      })
      .addCase(submitProductReplacement.rejected, (state, action) => {
        state.submissionStatus = "rejected";
        state.submissionError =
          action.payload || action.error.message || "Failed to replace product.";
      });
  },
});

export const { resetReplacementMutation } = dispatchReplacementSlice.actions;
export default dispatchReplacementSlice.reducer;
